// Build: 2026-04-25T00:00:00Z
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom';
import { Home, Search as SearchIcon, PlusCircle, Bell, User } from 'lucide-react';
import api, { isTokenExpired, clearLogin } from './api';
import Feed from './pages/Feed';
import PostDetail from './pages/PostDetail';
import Auth from './pages/Auth';
import Search from './pages/Search';
import EvidencePage from './pages/EvidencePage';
import IncidentDetail from './pages/IncidentDetail';
import OnboardingModal from './components/OnboardingModal';

// ── Mobile detection hook ────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

// ── Search bar ───────────────────────────────────────────────────────────────
function NavSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [trending, setTrending] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handle = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  useEffect(() => {
    if (open && !query.trim()) {
      api.get('/topics/trending').then(r => setTrending(r.data.topics || [])).catch(() => {});
    }
  }, [open, query]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim()) { setResults(null); return; }
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      api.post('/search', { query: val, type: 'all', limit: 5 })
        .then(r => setResults(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 300);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && query.trim()) {
      setOpen(false);
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
    if (e.key === 'Escape') setOpen(false);
  };

  const VERDICT_COLOR = {
    verified: 'var(--color-verified)',
    invalidated: 'var(--color-invalidated)',
    disputed: 'var(--color-disputed)',
    new: 'var(--color-new)',
  };
  const VERDICT_MARK = { verified: '✓', invalidated: '✗', disputed: '~', new: '•' };

  const postVerdict = (p) => {
    if (!p) return 'new';
    if (p.is_new) return 'new';
    if (!p.verdict || p.verdict === 'new') return 'new';
    return p.verdict;
  };

  const hasResults = results && (results.posts?.length || results.users?.length || results.topics?.length);

  const dropdownItemStyle = {
    padding: '9px 14px',
    cursor: 'pointer',
    color: 'var(--color-text-secondary)',
    fontSize: 13,
    fontFamily: 'var(--font-body)',
    transition: 'background 0.1s',
  };

  const sectionLabelStyle = {
    padding: '8px 14px 4px',
    margin: 0,
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontFamily: 'var(--font-body)',
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', flex: 1, maxWidth: 380, minWidth: 0 }}>
      <input
        type="text"
        placeholder="Search posts, topics, users..."
        value={query}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        style={{
          width: '100%',
          padding: '7px 14px',
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
          borderRadius: 20,
          color: 'var(--color-text-primary)',
          fontSize: 13,
          outline: 'none',
          boxSizing: 'border-box',
          fontFamily: 'var(--font-body)',
          transition: 'border-color 0.15s ease',
        }}
        onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--color-border-strong)')}
        onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--color-border)')}
      />
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
          zIndex: 500,
          maxHeight: 420,
          overflowY: 'auto',
        }}>
          {!query.trim() && trending.length > 0 && (
            <div>
              <p style={sectionLabelStyle}>Trending topics</p>
              {trending.map(t => (
                <div key={t.id}
                  onClick={() => { setOpen(false); navigate(`/posts/${t.root_post_id}`); }}
                  style={dropdownItemStyle}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-surface-hover)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  {t.topic_summary}
                  <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>{t.recent_argument_count} recent</span>
                </div>
              ))}
            </div>
          )}
          {loading && <p style={{ padding: '12px 14px', color: 'var(--color-text-muted)', fontSize: 13, margin: 0, fontFamily: 'var(--font-body)' }}>Searching…</p>}
          {!loading && hasResults && (
            <>
              {results.posts?.length > 0 && (
                <div>
                  <p style={sectionLabelStyle}>Posts</p>
                  {results.posts.map(p => {
                    const v = postVerdict(p);
                    return (
                      <div key={p.id}
                        onClick={() => { setOpen(false); navigate(`/posts/${p.id}`); }}
                        style={{ ...dropdownItemStyle, display: 'flex', alignItems: 'center', gap: 8 }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-surface-hover)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        <span style={{ color: VERDICT_COLOR[v], fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{VERDICT_MARK[v]}</span>
                        <span style={{ color: 'var(--color-text-primary)', fontSize: 13, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                          {p.post_meta?.og_title || p.content?.slice(0, 80)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {results.users?.length > 0 && (
                <div>
                  <p style={sectionLabelStyle}>Users</p>
                  {results.users.map(u => (
                    <div key={u.id} style={{ ...dropdownItemStyle, color: 'var(--color-text-secondary)' }}>@{u.username}</div>
                  ))}
                </div>
              )}
              {results.topics?.length > 0 && (
                <div>
                  <p style={sectionLabelStyle}>Topics</p>
                  {results.topics.map(t => (
                    <div key={t.id}
                      onClick={() => { setOpen(false); navigate(`/posts/${t.root_post_id}`); }}
                      style={dropdownItemStyle}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-surface-hover)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      {t.topic_summary}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ borderTop: '1px solid var(--color-border)', padding: '8px 14px' }}>
                <button
                  onClick={() => { setOpen(false); navigate(`/search?q=${encodeURIComponent(query.trim())}`); }}
                  style={{ background: 'transparent', border: 'none', color: 'var(--color-brand)', cursor: 'pointer', fontSize: 13, padding: 0, fontFamily: 'var(--font-body)', fontWeight: 600 }}
                >
                  See all results →
                </button>
              </div>
            </>
          )}
          {!loading && query.trim() && !hasResults && (
            <p style={{ padding: '12px 14px', color: 'var(--color-text-muted)', fontSize: 13, margin: 0, fontFamily: 'var(--font-body)' }}>No results for "{query}"</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Mobile compose overlay ───────────────────────────────────────────────────
function MobileCompose({ user, onClose }) {
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!content.trim()) return;
    if (!user) { navigate('/auth'); onClose(); return; }
    setSubmitting(true);
    try {
      await api.post('/posts', { content });
      onClose();
      navigate('/');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to post');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1500,
      background: 'var(--color-surface)',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: '1px solid var(--color-border)',
      }}>
        <button onClick={onClose} style={{
          background: 'transparent', border: 'none',
          color: 'var(--color-text-muted)', fontSize: 18, cursor: 'pointer',
        }}>✕</button>
        <span style={{
          color: 'var(--color-text-primary)', fontWeight: 700, fontSize: 16,
          fontFamily: 'var(--font-body)',
        }}>New Claim</span>
        <button
          onClick={handleSubmit}
          disabled={submitting || !content.trim()}
          style={{
            background: content.trim() ? 'var(--color-brand)' : 'var(--color-border)',
            color: '#fff', border: 'none', borderRadius: 20,
            padding: '7px 18px', fontSize: 14, fontWeight: 700,
            cursor: content.trim() ? 'pointer' : 'default',
            fontFamily: 'var(--font-body)',
          }}
        >
          {submitting ? '...' : 'Post'}
        </button>
      </div>
      <textarea
        autoFocus
        placeholder="Make a claim or share an article..."
        value={content}
        onChange={e => setContent(e.target.value)}
        style={{
          flex: 1, padding: 20,
          background: 'transparent', border: 'none', outline: 'none',
          color: 'var(--color-text-primary)', fontSize: 17,
          fontFamily: 'var(--font-body)', resize: 'none', lineHeight: 1.6,
        }}
      />
    </div>
  );
}

// ── Mobile bottom tab bar ────────────────────────────────────────────────────
function MobileTabBar({ user, onPostClick }) {
  const navigate = useNavigate();
  const tabs = [
    { Icon: Home,        label: 'Feed',    action: () => navigate('/') },
    { Icon: SearchIcon,  label: 'Search',  action: () => navigate('/search?q=') },
    { Icon: PlusCircle,  label: 'Post',    action: onPostClick, big: true },
    { Icon: Bell,        label: 'Alerts',  disabled: true, tooltip: 'Coming soon' },
    user
      ? { Icon: User,   label: 'Profile', disabled: true, tooltip: 'Coming soon' }
      : { Icon: User,   label: 'Sign In', action: () => navigate('/auth') },
  ];
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0,
      background: 'var(--color-surface)',
      borderTop: '1px solid var(--color-border)',
      display: 'flex', zIndex: 1000,
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {tabs.map(({ Icon, label, action, big, disabled, tooltip }) => (
        <div
          key={label}
          title={tooltip || label}
          onClick={disabled ? undefined : action}
          style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            padding: '8px 0', cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.35 : 1,
          }}
        >
          {big ? (
            <div style={{
              width: 42, height: 42, borderRadius: '50%',
              background: 'var(--color-brand)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon size={20} color="#fff" strokeWidth={2} />
            </div>
          ) : (
            <>
              <Icon size={20} color="var(--color-text-muted)" strokeWidth={1.5} />
              <span style={{
                fontSize: 10, color: 'var(--color-text-muted)',
                marginTop: 3, fontFamily: 'var(--font-body)',
              }}>{label}</span>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

// After logging in, go back to the page the user was on when their login
// expired (only paths on this site).
function safeNext() {
  try {
    const next = new URLSearchParams(window.location.search).get('next');
    return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
  } catch {
    return '/';
  }
}

// ── App (inside Router context) ──────────────────────────────────────────────
console.log('[App] Version: sprint-s1-s2-s3', new Date().toISOString());
function AppInner() {
  const [user, setUser] = useState(() => {
    // An expired login is cleared on load, so the site never shows someone
    // as logged in when the server will reject them (Move 5).
    try {
      if (isTokenExpired(localStorage.getItem('token'))) { clearLogin(); return null; }
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [showCompose, setShowCompose] = useState(false);
  const isMobile = useIsMobile();

  const login = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const completeOnboarding = () => {
    const updated = { ...user, onboarding_completed: true };
    localStorage.setItem('user', JSON.stringify(updated));
    setUser(updated);
  };

  return (
    <div style={{ minHeight: '100vh', paddingBottom: isMobile ? 64 : 0, background: 'var(--color-bg)' }}>
      {/* Top nav */}
      <nav style={{
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        padding: isMobile ? '0 12px' : '0 24px',
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', height: 56, gap: 12,
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <Link to="/" style={{
          color: 'var(--color-brand)',
          fontFamily: 'var(--font-display)',
          fontWeight: 700,
          fontSize: isMobile ? 18 : 22,
          textDecoration: 'none',
          flexShrink: 0,
          letterSpacing: '-0.02em',
        }}>
          IsItTrueNow
        </Link>
        <NavSearch />
        {!isMobile && (
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexShrink: 0 }}>
            {user ? (
              <>
                <span style={{
                  color: 'var(--color-text-secondary)',
                  fontSize: 13,
                  fontFamily: 'var(--font-body)',
                }}>
                  {user.username}
                </span>
                <button
                  onClick={logout}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--color-border)',
                    borderRadius: 6,
                    color: 'var(--color-text-secondary)',
                    padding: '5px 12px',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontFamily: 'var(--font-body)',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-border-strong)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; }}
                >
                  Log out
                </button>
              </>
            ) : (
              <Link to="/auth" style={{
                color: 'var(--color-brand)',
                fontSize: 13,
                fontFamily: 'var(--font-body)',
                fontWeight: 600,
              }}>
                Sign in
              </Link>
            )}
          </div>
        )}
      </nav>

      <main style={{ maxWidth: 680, margin: '0 auto', padding: isMobile ? '12px 10px' : '28px 16px' }}>
        <Routes>
          <Route path="/" element={<Feed user={user} />} />
          <Route path="/posts/:id" element={<PostDetail user={user} />} />
          <Route path="/posts/:id/a/:argId" element={<PostDetail user={user} />} />
          <Route path="/posts/:postId/evidence/:evidenceId" element={<EvidencePage user={user} />} />
          <Route path="/incidents/:nodeId" element={<IncidentDetail user={user} />} />
          <Route path="/auth" element={user ? <Navigate to={safeNext()} /> : <Auth onLogin={login} />} />
          <Route path="/search" element={<Search />} />
        </Routes>
      </main>

      {isMobile && <MobileTabBar user={user} onPostClick={() => setShowCompose(true)} />}
      {showCompose && <MobileCompose user={user} onClose={() => setShowCompose(false)} />}
      {user && user.onboarding_completed === false && (
        <OnboardingModal onComplete={completeOnboarding} />
      )}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}
